import {
  always,
  type Boost,
  filterKey,
  keyDomainFor,
  type Predicate,
  type SearchDescriptor,
  type SearchState,
} from '@shared/core/search';
import { useEffect, useMemo, useRef, useState } from 'react';

import { retryOnRefusedKey } from '../providers/retryOnRefusedKey';
import { useSearchCredentials } from '../providers/SearchProvider';
import { createSearchTransport, domainKey, type SearchTransport } from '../transport';

export const SEARCH_DEBOUNCE_MS = 150;

export interface UseSearchArgs<TDoc> {
  readonly descriptor: SearchDescriptor<TDoc>;
  readonly query: string;
  readonly filter?: Predicate<TDoc>;
  readonly hitsPerPage?: number;
  readonly boosts?: readonly Boost<TDoc>[];
  readonly enabled?: boolean;
  readonly retryKey?: number;
}

export interface UseSearchResult<TDoc> {
  readonly hits: readonly TDoc[];
  readonly state: SearchState | 'loading';
}

type Settled<TDoc> =
  | { readonly key: string; readonly scope: string; readonly status: 'ok' | 'empty'; readonly hits: readonly TDoc[] }
  | { readonly key: string; readonly scope: string; readonly status: 'unavailable'; readonly hits: readonly never[] };

const NO_HITS: readonly never[] = Object.freeze([]);

export function useSearch<TDoc>({
  descriptor,
  query,
  filter,
  hitsPerPage,
  boosts,
  enabled = true,
  retryKey = 0,
}: UseSearchArgs<TDoc>): UseSearchResult<TDoc> {
  const { credentials, state: credentialState, refresh } = useSearchCredentials();
  const collection = descriptor.indexName.typesense;
  const pageSize = hitsPerPage ?? descriptor.defaults.hitsPerPage;
  const predicate = filter ?? always<TDoc>();
  const predicateKey = filterKey(predicate);
  const boostsKey = boosts === undefined ? '' : JSON.stringify(boosts);

  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    if (query === debouncedQuery) return;
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, debouncedQuery]);

  const forbidden =
    credentials !== null &&
    (domainKey(credentials, keyDomainFor(collection)) === undefined ||
      (credentials.collections !== undefined && !credentials.collections.includes(collection)));
  const engine = credentials?.engine ?? null;
  const active = enabled && engine !== null && !forbidden;

  const scopeKey = useMemo(
    () => JSON.stringify([descriptor.entity, predicateKey, boostsKey, engine]),
    [descriptor.entity, predicateKey, boostsKey, engine]
  );

  const requestKey = useMemo(
    () =>
      JSON.stringify([
        'search',
        descriptor.entity,
        debouncedQuery,
        predicateKey,
        boostsKey,
        pageSize,
        engine,
        retryKey,
      ]),
    [descriptor.entity, debouncedQuery, predicateKey, boostsKey, pageSize, engine, retryKey]
  );

  const latest = useRef({ credentials, refresh, descriptor, predicate, boosts });
  useEffect(() => {
    latest.current = { credentials, refresh, descriptor, predicate, boosts };
  });

  const [settled, setSettled] = useState<Settled<TDoc> | null>(null);

  useEffect(() => {
    if (!active) {
      setSettled(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const searchOn = (transport: SearchTransport) =>
      transport.search<TDoc>({
        descriptor: latest.current.descriptor,
        query: debouncedQuery,
        filter: latest.current.predicate,
        hitsPerPage: pageSize,
        boosts: latest.current.boosts,
        signal: controller.signal,
      });

    const run = async () => {
      const credentials = latest.current.credentials;
      const transport = credentials === null ? null : createSearchTransport(credentials, collection);
      if (transport === null) {
        setSettled({ key: requestKey, scope: scopeKey, status: 'unavailable', hits: NO_HITS });
        return;
      }
      try {
        const hits = await retryOnRefusedKey({
          credentials,
          send: () => searchOn(transport),
          remint: () => latest.current.refresh(),
          resend: (fresh) => {
            // A predicate built for one engine must not reach the other: an org flipped mid re-mint waits for the next query.
            if (fresh.engine !== credentials?.engine) return null;
            const freshTransport = createSearchTransport(fresh, collection);
            return freshTransport === null ? null : searchOn(freshTransport);
          },
          isCurrent: () => !cancelled,
        });
        if (!cancelled)
          setSettled({ key: requestKey, scope: scopeKey, status: hits.length === 0 ? 'empty' : 'ok', hits });
      } catch {
        if (!cancelled) setSettled({ key: requestKey, scope: scopeKey, status: 'unavailable', hits: NO_HITS });
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [active, requestKey, scopeKey, collection, debouncedQuery, pageSize]);

  if (!enabled) return { hits: NO_HITS, state: 'empty' };
  if (credentialState === 'loading') return { hits: NO_HITS, state: 'loading' };
  if (credentialState === 'unavailable' || credentials === null) return { hits: NO_HITS, state: 'unavailable' };
  if (forbidden) return { hits: NO_HITS, state: 'forbidden' };
  if (settled === null) return { hits: NO_HITS, state: 'loading' };
  if (settled.key !== requestKey || debouncedQuery !== query) {
    return { hits: settled.scope === scopeKey ? settled.hits : NO_HITS, state: 'loading' };
  }
  return { hits: settled.hits, state: settled.status };
}
